import { t } from '@lingui/macro';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { noddiAsync, URLKeys } from 'noddi-async';
import { paymentMethod, quoteItemStatus, QuoteItemStatusType, TireOfferV2 } from 'noddi-types';
import { NoddiButton, themeColors } from 'noddi-ui-web';
import { customerRoutes, DateFormats, noddiFormatDate } from 'noddi-util';
import { useNavigate } from 'react-router-dom';
import { shouldPayForOrder } from './utils';

interface Step {
  label: string;
  description: string;
  key: (QuoteItemStatusType | 'ORDER_PROCESSING')[];
}

function getStepsWithTranslation({
  estimatedDeliveryDate,
  orderAcceptedButShouldPay
}: {
  estimatedDeliveryDate?: string;
  orderAcceptedButShouldPay: boolean;
}): Step[] {
  const estimatedDeliveryDateFormatted = noddiFormatDate(estimatedDeliveryDate, DateFormats.SHORT_DAY_SHORT_MONTH);
  const translatedDescription = estimatedDeliveryDateFormatted
    ? `. ${t`They are estimated to arrive at our warehouse on`} ${estimatedDeliveryDateFormatted}`
    : '';

  return [
    {
      label: t`Order confirmed`,
      description: orderAcceptedButShouldPay
        ? t`Your order has been confirmed. Please pay for your order in order for us to process it.`
        : t`Your order has been confirmed and paid.`,
      key: [quoteItemStatus.ACCEPTED_WITH_PAYMENT, quoteItemStatus.ACCEPTED_WAITING_PAYMENT]
    },
    {
      label: t`Order processing`,
      description: t`Our team will are processing your order. You will receive an SMS with estimated delivery time when the tires are ordered from our main warehouse.`,
      key: ['ORDER_PROCESSING']
    },
    {
      label: t`Your tires are on the way`,
      description: t`Your tires are on the way` + translatedDescription,
      key: [
        quoteItemStatus.INVENTORY_ORDERED_AT_SUPPLIERS_IN_FULL,
        quoteItemStatus.INVENTORY_ORDERED_AT_SUPPLIERS_PARTIAL
      ]
    },
    {
      label: t`Tires arrived`,
      description: t`Your tires have arrived. We will contact you to book an appointment to have them mounted on your car.`,
      key: [quoteItemStatus.INVENTORY_RECEIVED_IN_FULL, quoteItemStatus.INVENTORY_RECEIVED_PARTIAL]
    },
    {
      label: t`Tires mounted`,
      description: t`The tires have been mounted on the your car.`,
      key: [quoteItemStatus.TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY]
    }
  ];
}

interface StatusUpdatesProps {
  tireOffer: TireOfferV2;
}

export const StatusUpdates = ({ tireOffer }: StatusUpdatesProps) => {
  const navigate = useNavigate();
  const selectedQuoteItem = tireOffer.quoteItems.find((quoteItem) => quoteItem.isSelected);

  const isProcessing =
    tireOffer.status.status === quoteItemStatus.ACCEPTED_WITH_PAYMENT ||
    (tireOffer.status.status === quoteItemStatus.ACCEPTED_WAITING_PAYMENT &&
      tireOffer.paymentMethod === paymentMethod.INVOICE);

  const status = isProcessing ? 'ORDER_PROCESSING' : tireOffer.status.status;

  const orderAcceptedButShouldPay = shouldPayForOrder({
    status: tireOffer.status.status,
    paymentMethodInTireOffer: tireOffer.paymentMethod
  });

  const steps = getStepsWithTranslation({
    estimatedDeliveryDate: selectedQuoteItem?.quoteItemTires[0]?.inventoryOrder?.estimatedDeliveryDate,
    orderAcceptedButShouldPay
  });
  const activeStep = steps.findIndex((step) => step.key.includes(status));

  const { mutateAsync: createOrderShopDinteroSession, isPending: isCreateOrderShopDinteroSessionPending } =
    noddiAsync.usePost({
      type: URLKeys.postCreateOrderShopDinteroSession,
      queryConfig: {
        onSuccess: async (data) => {
          navigate(
            customerRoutes.tireOfferConfirmation.getPath({
              sessionId: data.data.sessionId
            })
          );
        }
      }
    });

  const handleCreateOrderShopDinteroSession = async () => {
    if (tireOffer.orderShop?.id) {
      await createOrderShopDinteroSession({ orderId: tireOffer.orderShop.id });
    }
  };

  return (
    <Stepper
      activeStep={activeStep}
      orientation='vertical'
      sx={{
        '& .MuiStepConnector-vertical': {
          backgroundColor: 'transparent'
        },
        '& .MuiStepIcon-root.Mui-active': {
          color: themeColors['card-selected-background']
        },
        '& .MuiStepIcon-root.Mui-completed': {
          color: themeColors.primary
        }
      }}
    >
      {steps.map((step) => (
        <Step expanded key={step.label}>
          <StepLabel>{step.label}</StepLabel>
          <StepContent>
            <p>{step.description}</p>

            {orderAcceptedButShouldPay && step.key.includes(quoteItemStatus.ACCEPTED_WITH_PAYMENT) && (
              <NoddiButton
                loading={isCreateOrderShopDinteroSessionPending}
                className='mt-4'
                onClick={handleCreateOrderShopDinteroSession}
              >
                {t`Pay now`}
              </NoddiButton>
            )}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
};
